import React from "react";
import { Product } from "../TemplateInterface";
import { useEnhancedCart } from "@/contexts/EnhancedCartContext";
import { ShoppingCart } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatPrice as formatPriceUtil } from "@/utils/currency";

interface ProductCardProps {
  product: Product;
  variant?: "default" | "list";
  currency?: string;
  themeColors?: {
    primary?: string;
    secondary?: string;
    primaryColor?: string;
    secondaryColor?: string;
  };
  onProductClick?: (product: Product) => void;
}

function AddToCartButton({
  primaryColor,
  onClick,
  className,
  fullWidth = true,
}: {
  primaryColor: string;
  onClick: (e: React.MouseEvent) => void;
  className?: string;
  fullWidth?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "group relative overflow-hidden rounded-full border py-2.5 px-3 text-xs sm:text-sm font-semibold",
        "transition-shadow duration-300 hover:cursor-pointer",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
        fullWidth && "w-full",
        className,
      )}
      style={
        {
          borderColor: primaryColor,
          color: primaryColor,
          ["--btn-primary"]: primaryColor,
        } as React.CSSProperties
      }
    >
      <span
        className="absolute inset-0 origin-left scale-x-0 bg-[var(--btn-primary)] transition-transform duration-300 ease-out group-hover:scale-x-100 group-active:scale-x-100 group-focus-visible:scale-x-100"
        aria-hidden
      />
      <span className="relative z-10 flex items-center justify-center gap-1.5 transition-colors duration-300 group-hover:text-white group-active:text-white group-focus-visible:text-white">
        <ShoppingCart className="h-3.5 w-3.5 sm:h-4 sm:w-4 shrink-0" />
        Add to Cart
      </span>
    </button>
  );
}

export function ProductCard({
  product,
  variant = "default",
  currency = "NGN",
  themeColors = { primaryColor: "#3b82f6", secondaryColor: "#10b981" },
  onProductClick,
}: ProductCardProps) {
  const { addToCart } = useEnhancedCart();
  const primaryColor =
    themeColors.primaryColor || themeColors.primary || "#3b82f6";

  const handleProductClick = () => {
    if (onProductClick) {
      onProductClick(product);
    }
  };

  const formatPrice = (price: number) =>
    formatPriceUtil(price, { currency });

  const handleAddToCart = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (
      (product.variants && product.variants.length > 0) ||
      (product.options && product.options.length > 0)
    ) {
      if (onProductClick) {
        onProductClick(product);
      }
      return;
    }

    addToCart(product, 1);
  };

  const priceBlock = (
    <div>
      <div
        className="font-semibold text-sm sm:text-base"
        style={{ color: primaryColor }}
      >
        {formatPrice(product.price)}
      </div>
      <div className="min-h-[1.125rem]">
        {product.compareAtPrice ? (
          <div className="text-xs line-through text-black/40">
            {formatPrice(product.compareAtPrice)}
          </div>
        ) : null}
      </div>
      {product.wholesaleEnabled &&
        typeof product.wholesaleMinQuantity === "number" && (
          <div className="mt-0.5 text-[10px] text-black/40">
            Wholesale · min {product.wholesaleMinQuantity} units
          </div>
        )}
    </div>
  );

  if (variant === "list") {
    return (
      <div
        className="flex h-full cursor-pointer items-stretch overflow-hidden rounded-2xl bg-white ring-1 ring-black/5 transition-shadow hover:shadow-[0_16px_40px_-24px_rgba(0,0,0,0.2)]"
        onClick={handleProductClick}
      >
        {product.images?.[0] ? (
          <div className="relative w-24 shrink-0 overflow-hidden sm:w-32">
            <img
              src={product.images[0].url}
              alt={product.images[0].alt || product.name}
              className="h-full w-full object-cover"
            />
          </div>
        ) : (
          <div
            className="flex w-24 shrink-0 items-center justify-center sm:w-32"
            style={{ backgroundColor: `${primaryColor}12` }}
          />
        )}

        <div className="flex min-h-0 flex-1 flex-col p-3 sm:p-4">
          <div>
            <div className="mb-1 flex items-center gap-2">
              <h3 className="text-sm font-semibold sm:text-base">{product.name}</h3>
              {product.category && (
                <span
                  className="shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold text-white"
                  style={{ backgroundColor: primaryColor }}
                >
                  {product.category}
                </span>
              )}
            </div>
            {product.description && (
              <p className="mt-1 line-clamp-2 text-xs text-black/45 sm:text-sm">
                {product.description}
              </p>
            )}
          </div>

          <div className="min-h-2 flex-1" />

          <div className="mt-auto flex items-end justify-between gap-3">
            {priceBlock}
            <AddToCartButton
              primaryColor={primaryColor}
              onClick={handleAddToCart}
              fullWidth={false}
              className="shrink-0"
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex h-full cursor-pointer flex-col overflow-hidden rounded-2xl bg-white ring-1 ring-black/5 transition-shadow hover:shadow-[0_16px_40px_-24px_rgba(0,0,0,0.2)]"
      onClick={handleProductClick}
    >
      <div className="relative shrink-0">
        {product.images?.[0] ? (
          <div className="aspect-square overflow-hidden bg-black/[0.03]">
            <img
              src={product.images[0].url}
              alt={product.images[0].alt || product.name}
              className="h-full w-full object-cover transition-transform duration-500 hover:scale-[1.03]"
            />
          </div>
        ) : (
          <div
            className="aspect-square"
            style={{ backgroundColor: `${primaryColor}12` }}
          />
        )}

        {product?.category && (
          <div
            className="absolute right-2.5 top-2.5 rounded-full px-2.5 py-1 text-[10px] font-semibold text-white"
            style={{ backgroundColor: primaryColor }}
          >
            {product.category}
          </div>
        )}
      </div>

      <div className="flex min-h-0 flex-1 flex-col p-3 sm:p-4">
        <h3 className="mb-1 truncate text-sm font-semibold sm:text-base">
          {product.name}
        </h3>

        <div className="mt-auto shrink-0">
          {priceBlock}
          <AddToCartButton
            primaryColor={primaryColor}
            onClick={handleAddToCart}
            className="mt-3"
          />
        </div>
      </div>
    </div>
  );
}
